import { useEffect, useState } from 'react';

/**
 * Sterktemeter op zxcvbn-ts, de bibliotheek die de OWASP Authentication Cheat
 * Sheet zelf aanraadt. Die schat hoeveel pogingen raden zou kosten, in plaats
 * van te tellen of er een hoofdletter en een cijfer in zitten. `Welkom2026!`
 * krijgt daardoor terecht een lage score en `blauwe olifant koopt kaas` een hoge.
 *
 * Dit is **advies, geen slot**. Wat hier gebeurt staat in de browser en is dus
 * te omzeilen; het afdwingen gebeurt aan de serverkant (lengte, geen naam of
 * e-mailadres erin, geen voorspelbaar woord, en de controle tegen datalekken).
 *
 * De woordenboeken wegen samen een paar honderd kilobyte, dus ze komen via een
 * dynamische import: Vite maakt daar een aparte chunk van die alleen op dit
 * scherm wordt opgehaald en nooit in de hoofdbundle terechtkomt.
 */

type Score = 0 | 1 | 2 | 3 | 4;
type Beoordelaar = (wachtwoord: string, extra: string[]) => Promise<{ score: Score }>;

const LABELS: Record<Score, string> = {
  0: 'Zeer zwak',
  1: 'Zwak',
  2: 'Redelijk',
  3: 'Sterk',
  4: 'Zeer sterk',
};

const KLEUREN: Record<Score, string> = {
  0: '#B0543B',
  1: '#B0543B',
  2: '#E0A943',
  3: '#3E7D63',
  4: '#3E7D63',
};

/** Wordt één keer opgebouwd en daarna hergebruikt, ook tussen renders. */
let beoordelaar: Promise<Beoordelaar> | null = null;

function laadBeoordelaar(): Promise<Beoordelaar> {
  beoordelaar ??= (async () => {
    const [{ ZxcvbnFactory }, common, nl] = await Promise.all([
      import('@zxcvbn-ts/core'),
      import('@zxcvbn-ts/language-common'),
      import('@zxcvbn-ts/language-nl-be'),
    ]);

    // Het Nederlandse pak is de Belgische variant; dat is de enige die er is en
    // de woordenlijsten zijn voor dit doel hetzelfde. De vertalingen komen
    // eruit, zodat eventuele uitleg in het Nederlands is.
    const zxcvbn = new ZxcvbnFactory({
      graphs: common.adjacencyGraphs,
      dictionary: { ...common.dictionary, ...nl.dictionary },
      translations: nl.translations,
    });

    return async (wachtwoord, extra) => {
      const uitslag = await zxcvbn.checkAsync(wachtwoord, extra);
      return { score: uitslag.score as Score };
    };
  })();
  return beoordelaar;
}

export function WachtwoordMeter({
  wachtwoord,
  /** Naam en e-mailadres wegen mee: die horen niet in het wachtwoord te zitten. */
  eigenWoorden = [],
}: {
  wachtwoord: string;
  eigenWoorden?: string[];
}) {
  const [score, setScore] = useState<Score | null>(null);

  // `eigenWoorden` komt bij elke render als nieuwe array binnen, dus de identiteit
  // ervan is geen bruikbare dependency: het effect zou dan bij elke toetsaanslag de
  // beoordelaar opnieuw aanroepen. De inhoud is wél stabiel, dus die wordt hier één
  // string — en anders dan een `join` in de dependency-lijst zelf kan de linter deze
  // statisch nalopen. Een NUL-teken als scheiding, want een naam of e-mailadres bevat
  // er nooit een; bij een `|` zou een woord met een pijp erin verkeerd terugkomen.
  const eigenWoordenSleutel = eigenWoorden.filter(Boolean).join('\0');

  useEffect(() => {
    if (!wachtwoord) {
      setScore(null);
      return;
    }
    let actueel = true;
    void laadBeoordelaar()
      .then((beoordeel) =>
        beoordeel(wachtwoord, eigenWoordenSleutel ? eigenWoordenSleutel.split('\0') : []),
      )
      .then((uitslag) => {
        // Tijdens het typen komen antwoorden soms uit de pas; alleen het laatste
        // verzoek mag de score zetten.
        if (actueel) setScore(uitslag.score);
      })
      // Lukt het laden niet, dan verdwijnt alleen de meter. De serverregels
      // blijven staan, dus dat is geen blokkade.
      .catch(() => actueel && setScore(null));
    return () => {
      actueel = false;
    };
  }, [wachtwoord, eigenWoordenSleutel]);

  if (!wachtwoord) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 4 }} aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              height: 4,
              flex: 1,
              backgroundColor: score !== null && i <= score ? KLEUREN[score] : '#D6D4CB',
            }}
          />
        ))}
      </div>
      <div style={{ fontSize: 12, color: '#444444' }} role="status">
        {score === null ? 'Sterkte berekenen…' : `Sterkte: ${LABELS[score]}`}
      </div>
    </div>
  );
}
